// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { Alert, Button, Drawer, Select, Sortable } from '@imprivata-cloud/design-system';
import { Spin } from 'antd';
import { Lock, Plus, Trash2 } from 'lucide-react';
import { useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { InstanceKey } from '../constants';
import { encode } from './codec/codec';
import { toBlocks } from './codec/order';
import './Conditions.less';
import { moveFromDragEnd } from './dragEnd';
import {
  canAddCondition,
  conditionOptions,
  editorReducer,
  seedEditorState,
  selectedRiskTokens,
} from './editor-reducer';
import {
  actionLabelKey,
  ALERT_KEYS,
  APPLICATION_LABEL_KEYS,
  CONDITION_LABEL_KEYS,
  CONTROL_KEYS,
  TEMPLATE_KEYS,
} from './labels';
import type { RiskLevelOption } from '@api/types';
import type { Condition, ConditionOption, Rule } from './types';
import { type RuleSet, useRulesMutation, useRulesQuery } from './useRules';

// PROVISIONAL: the rules contract has not pinned the stale-write status. Any other status falls back
// to the generic save error.
const RULE_CONFLICT_STATUS = 409;

const DRAWER_WIDTH = '690px';
const GLYPH_SIZE = 16;

// Capabilities are optional on the wire; both readers fail closed.
const granted = (capability: boolean | undefined): boolean => capability === true;
const conditionIsLocked = (rule: Rule): boolean => rule.caps.conditionLocked !== false;

const ruleLabelKey = (rule: Rule): string =>
  rule.kind === 'otherwise'
    ? CONTROL_KEYS.otherwiseCondition
    : CONDITION_LABEL_KEYS[rule.condition];

type ConditionsDrawerProps = {
  instanceKey: InstanceKey;
  open: boolean;
  onClose: () => void;
  entitled: boolean;
  riskAwarenessOn: boolean;
  riskLevels: RiskLevelOption[];
};

export const ConditionsDrawer = ({
  instanceKey,
  open,
  onClose,
  entitled,
  riskAwarenessOn,
  riskLevels,
}: ConditionsDrawerProps) => {
  const { t } = useTranslation();

  // Bumping the seed remounts the editor: edits are discarded and the served rules and version adopted.
  const [seedId, setSeedId] = useState(0);
  const query = useRulesQuery(instanceKey, open);

  const handleClose = () => {
    setSeedId(id => id + 1);
    onClose();
  };

  const reseed = async () => {
    await query.refetch();
    setSeedId(id => id + 1);
  };

  return (
    // The wrapper swallows mask clicks by default. Escape is left to dnd-kit, which cancels drags with it.
    <Drawer
      open={open}
      disablePointerDismissal={false}
      onOpenChange={(next: boolean, event: { reason: string }) => !next && event.reason !== 'escape-key' && handleClose()}
    >
      <Drawer.Panel
        title={t(TEMPLATE_KEYS.drawerTitle, {
          application: t(APPLICATION_LABEL_KEYS[instanceKey]),
        })}
        width={DRAWER_WIDTH}
      >
        {query.isLoading && <Spin />}

        {/* Keyed by error time, so a new failure re-shows a dismissed alert. */}
        {query.isError && (
          <LoadFailedAlert key={query.errorUpdatedAt} onRefresh={() => void query.refetch()} />
        )}

        {query.data !== undefined && (
          <ConditionsEditor
            key={seedId}
            instanceKey={instanceKey}
            ruleSet={query.data}
            riskSelectable={entitled && riskAwarenessOn}
            riskLevels={riskLevels}
            onSaved={onClose}
            onRefresh={() => void reseed()}
            onCancel={handleClose}
          />
        )}
      </Drawer.Panel>
    </Drawer>
  );
};

const LoadFailedAlert = ({ onRefresh }: { onRefresh: () => void }) => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) { return null; }

  return (
    <div className="conditions-alerts">
      <Alert
        status="error"
        title={t(ALERT_KEYS.loadFailedTitle)}
        message={t(ALERT_KEYS.loadFailedMessage)}
        actions={
          <Button variant="text" onClick={onRefresh}>
            {t(ALERT_KEYS.refresh)}
          </Button>
        }
        onClose={() => setDismissed(true)}
      />
    </div>
  );
};

type ConditionsEditorProps = {
  instanceKey: InstanceKey;
  ruleSet: RuleSet;
  riskSelectable: boolean;
  riskLevels: RiskLevelOption[];
  onSaved: () => void;
  onRefresh: () => void;
  onCancel: () => void;
};

const ConditionsEditor = ({
  instanceKey,
  ruleSet,
  riskSelectable,
  riskLevels,
  onSaved,
  onRefresh,
  onCancel,
}: ConditionsEditorProps) => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(editorReducer, ruleSet.decoded, seedEditorState);
  const [announcement, setAnnouncement] = useState('');
  const mutation = useRulesMutation(instanceKey);

  const blocks = toBlocks(state.rules);
  const selectedTokens = selectedRiskTokens(state.rules);
  const saveError = mutation.error;

  // Add condition renders before OTHERWISE. Both runs pass the reducer indices into the full list.
  const terminalAt = blocks.findIndex(block => block.kind === 'otherwiseBlock');
  const splitAt = terminalAt === -1 ? blocks.length : terminalAt;

  // A drop on itself or off the list is not a move and is not announced.
  const move = (blockIndex: number, blockRules: Rule[], from: number, to: number) => {
    if (from < 0 || to < 0 || to >= blockRules.length || from === to) { return; }

    dispatch({ type: 'moveWithinBlock', blockIndex, from, to });
    setAnnouncement(t(TEMPLATE_KEYS.reordered, { count: blockRules.length, position: to + 1 }));
  };

  const handleSave = () => {
    mutation.mutate(encode(ruleSet.resource, state), { onSuccess: onSaved });
  };

  const renderBlock = (block: (typeof blocks)[number], blockIndex: number) => {
    if (block.kind === 'tenantRuleBlock') {
      return (
        <TenantRuleBlock
          // Not the index: a delete can drop a whole block and shift its neighbours.
          key={block.rules.at(0)?.key}
          rules={block.rules}
          riskSelectable={riskSelectable}
          selectedTokens={selectedTokens}
          riskLevels={riskLevels}
          onMove={(from, to) => move(blockIndex, block.rules, from, to)}
          onCondition={(key, condition, conditionValue) =>
            dispatch({ type: 'setCondition', condition, conditionValue, key })
          }
          onAction={(key, action) => dispatch({ type: 'setAction', action, key })}
          onRemove={key => dispatch({ type: 'removeTenantRule', key })}
        />
      );
    }
    if (block.kind === 'systemRuleBlock') {
      return (
        <SystemRuleCard
          key={block.rule.key}
          rule={block.rule}
          riskSelectable={riskSelectable}
          selectedTokens={selectedTokens}
          riskLevels={riskLevels}
          onCondition={(condition, conditionValue) =>
            dispatch({ type: 'setCondition', condition, conditionValue, key: block.rule.key })
          }
          onAction={action => dispatch({ type: 'setAction', action, key: block.rule.key })}
        />
      );
    }
    return (
      <OtherwiseCard
        key={block.rule.key}
        rule={block.rule}
        onAction={action => dispatch({ type: 'setAction', action, key: block.rule.key })}
      />
    );
  };

  return (
    <>
      {saveError !== null && (
        <div className="conditions-alerts">
          {saveError.httpStatus === RULE_CONFLICT_STATUS ? (
            // Not dismissible: the edits cannot be saved over the newer rule set.
            <Alert
              status="error"
              title={t(ALERT_KEYS.conflictTitle)}
              message={t(ALERT_KEYS.conflictMessage)}
              actions={
                <Button variant="text" onClick={onRefresh}>
                  {t(ALERT_KEYS.refresh)}
                </Button>
              }
            />
          ) : (
            <Alert
              status="error"
              title={t(ALERT_KEYS.saveFailedTitle)}
              message={t(ALERT_KEYS.saveFailedMessage)}
              onClose={() => mutation.reset()}
            />
          )}
        </div>
      )}

      <div className="conditions-blocks">
        {blocks.slice(0, splitAt).map((block, blockIndex) => renderBlock(block, blockIndex))}

        <div className="conditions-add">
          <Button
            variant="text"
            size="sm"
            icon={<Plus size={GLYPH_SIZE} />}
            disabled={!canAddCondition(riskSelectable, riskLevels, state.rules)}
            onClick={() => dispatch({ type: 'addTenantRule', entitled: riskSelectable, riskLevels })}
          >
            {t(CONTROL_KEYS.addCondition)}
          </Button>
        </div>

        {blocks.slice(splitAt).map((block, offset) => renderBlock(block, splitAt + offset))}
      </div>

      {/* Clipped, not hidden: a hidden node is not announced. */}
      <p aria-live="polite" className="conditions-announcement">
        {announcement}
      </p>

      <div className="conditions-footer">
        <Button variant="default" onClick={onCancel}>
          {t(CONTROL_KEYS.cancel)}
        </Button>
        <Button
          variant="primary"
          loading={mutation.isPending}
          disabled={mutation.isPending}
          onClick={handleSave}
        >
          {t(mutation.isPending ? CONTROL_KEYS.saving : CONTROL_KEYS.save)}
        </Button>
      </div>
    </>
  );
};

type TenantRuleBlockProps = {
  rules: Rule[];
  riskSelectable: boolean;
  selectedTokens: Set<string>;
  riskLevels: RiskLevelOption[];
  onMove: (from: number, to: number) => void;
  onCondition: (key: string, condition: Condition, conditionValue: string | undefined) => void;
  onAction: (key: string, action: string) => void;
  onRemove: (key: string) => void;
};

const TenantRuleBlock = ({
  rules,
  riskSelectable,
  selectedTokens,
  riskLevels,
  onMove,
  onCondition,
  onAction,
  onRemove,
}: TenantRuleBlockProps) => {
  const { t } = useTranslation();

  return (
    <Sortable.Root
      className="conditions-sortable"
      onDragEnd={event => moveFromDragEnd(event, onMove)}
    >
      {rules.map((rule, index) => (
        <Sortable.Item key={rule.key} id={rule.key} index={index} label={t(ruleLabelKey(rule))}>
          <div className="conditions-card conditions-card--row">
            <ConditionSlot
              rule={rule}
              riskSelectable={riskSelectable}
              selectedTokens={selectedTokens}
              riskLevels={riskLevels}
              onChange={(condition, conditionValue) => onCondition(rule.key, condition, conditionValue)}
            />
            <ActionSlot rule={rule} onChange={action => onAction(rule.key, action)} />
            <div className="conditions-row-actions">
              <Button
                variant="glyph"
                icon={<Trash2 size={GLYPH_SIZE} />}
                aria-label={t(CONTROL_KEYS.removeCondition)}
                onClick={() => onRemove(rule.key)}
              >
                {''}
              </Button>
            </div>
          </div>
        </Sortable.Item>
      ))}
    </Sortable.Root>
  );
};

const SystemRuleCard = ({
  rule,
  riskSelectable,
  selectedTokens,
  riskLevels,
  onCondition,
  onAction,
}: {
  rule: Rule;
  riskSelectable: boolean;
  selectedTokens: Set<string>;
  riskLevels: RiskLevelOption[];
  onCondition: (condition: Condition, conditionValue: string | undefined) => void;
  onAction: (action: string) => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className="conditions-card">
      <p className="conditions-badge">
        <Lock size={GLYPH_SIZE} aria-hidden="true" />
        {t(CONTROL_KEYS.requiredRule)}
      </p>
      <ConditionSlot
        rule={rule}
        riskSelectable={riskSelectable}
        selectedTokens={selectedTokens}
        riskLevels={riskLevels}
        onChange={onCondition}
      />
      <ActionSlot rule={rule} onChange={onAction} />
    </div>
  );
};

const OtherwiseCard = ({ rule, onAction }: { rule: Rule; onAction: (action: string) => void }) => {
  const { t } = useTranslation();

  return (
    <div className="conditions-card">
      <ActionSlot rule={rule} label={t(CONTROL_KEYS.otherwiseCondition)} onChange={onAction} />
    </div>
  );
};

const ConditionSlot = ({
  rule,
  riskSelectable,
  selectedTokens,
  riskLevels,
  onChange,
}: {
  rule: Rule;
  riskSelectable: boolean;
  selectedTokens: Set<string>;
  riskLevels: RiskLevelOption[];
  onChange: (condition: Condition, conditionValue: string | undefined) => void;
}) => {
  const { t } = useTranslation();

  if (rule.kind === 'otherwise') { return null; }

  const ruleConditionValue = rule.kind === 'tenant' ? rule.conditionValue : undefined;
  const label =
    rule.condition === 'risk-level'
      ? (riskLevels.find(l => l.token === ruleConditionValue)?.label ??
        t(CONDITION_LABEL_KEYS[rule.condition]))
      : t(CONDITION_LABEL_KEYS[rule.condition]);

  if (conditionIsLocked(rule)) {
    return (
      <div className="conditions-control">
        <span className="conditions-control-label">{t(CONTROL_KEYS.conditionLabel)}</span>
        <p className="conditions-locked">{label}</p>
      </div>
    );
  }

  const selectedConditionValue = rule.kind === 'tenant' ? (rule.conditionValue ?? null) : null;
  const options = conditionOptions(true, riskLevels).filter(
    opt =>
      opt.conditionValue === selectedConditionValue ||
      (riskSelectable && !selectedTokens.has(opt.conditionValue)),
  );

  return (
    <div className="conditions-control">
      <span className="conditions-control-label">{t(CONTROL_KEYS.conditionLabel)}</span>
      <Select
        aria-label={t(CONTROL_KEYS.conditionLabel)}
        items={options.map((opt: ConditionOption) => ({
          label: opt.label,
          value: opt.conditionValue,
        }))}
        value={selectedConditionValue}
        disabled={!granted(rule.caps.enabled)}
        onSelectionChange={value => {
          const chosen = options.find((opt: ConditionOption) => opt.conditionValue === value);
          if (chosen !== undefined) { onChange(chosen.condition, chosen.conditionValue); }
        }}
      />
    </div>
  );
};

const ActionSlot = ({
  rule,
  label,
  onChange,
}: {
  rule: Rule;
  label?: string;
  onChange: (action: string) => void;
}) => {
  const { t } = useTranslation();

  const heading = label ?? t(CONTROL_KEYS.actionLabel);

  const items = rule.allowedValues.map(value => {
    const key = actionLabelKey(value);
    return { label: key === undefined ? '' : t(key), value };
  });

  return (
    <div className="conditions-control">
      <span className="conditions-control-label">{heading}</span>
      <Select
        aria-label={heading}
        items={items}
        value={rule.action ?? null}
        disabled={!granted(rule.caps.valueEditable) || !granted(rule.caps.enabled)}
        onSelectionChange={value =>
          value !== null && rule.allowedValues.includes(value) && onChange(value)
        }
      />
    </div>
  );
};
